// Makes the ORDER show the same line item properties the cart and checkout showed.
//
// Zepto prices paid options (the "(+60 kr.)" fields) through a Shopify cart-transform
// Function. Any line that machinery manages carries `_pplr_cart_variant` plus a snapshot
// property `_pplr_attribute_data`, and when the order is created the Function REPLACES the
// line's properties with the snapshot's content — verbatim, key for key (compare order
// #1016 with its cart: `Farve` arrived as `_Farve`, `_pplr_line_ref` as `__pplr_line_ref`).
// Zepto builds that snapshot by prefixing `_` to every property name
// (pplr_build_attribute_data in pplr_common.js), so on the order EVERYTHING is hidden:
// no measurements, no engraving mirror, no preview link on the order status page or in the
// order confirmation email. Products whose personalization is free (ATLAS) never enter the
// cart-transform path, which is why they alone kept a visible preview link — task 40.
//
// The snapshot is just a line item property, and edits to it stick: Zepto's own repair
// loop (pplr_repair_and_check_budget) only rebuilds a snapshot that is MISSING, and
// byRavn-gavepose.js already stamps and removes the Gavepose flag inside it. So this
// module rewrites the snapshot to hold the line's CURRENT properties verbatim — visible
// names stay visible, `_`-hidden names stay hidden, and a preview the strip removed
// stays removed. The post-swap order then matches the cart exactly, on every surface at
// once (order status page, confirmation email, admin).
//
// Same failure posture as byRavn-cart-preview-strip.js: any surprise leaves the line
// alone. The one invariant that stops everything is a changed item_count — a rewrite
// re-keys the line, and two lines differing only in snapshot content would merge.
(function () {
  const SNAPSHOT_KEY = '_pplr_attribute_data';
  const PUBLISH_SOURCE = 'byRavn-order-properties';
  const UNIT = String.fromCharCode(31);

  // Strictly after byRavn-cart-preview-strip.js on every trigger (400/700 ms there): the
  // strip re-keys lines and removes properties this module must fold into the snapshot,
  // so let it finish first and normalize its output instead of racing it.
  const SWEEP_DEBOUNCE_MS = 900;
  const MIN_SWEEP_INTERVAL_MS = 1500;
  const ZEPTO_ADD_DELAY_MS = 1400;
  const ZEPTO_ADD_RETRY_MS = 2000;
  const MAX_ATTEMPTS = 2;
  const MAX_LINES_PER_SWEEP = 10;
  const STORE_KEY = 'byRavn:orderProperties:attempts';
  const STORE_LIMIT = 50;

  let busy = false;
  let disabled = false;
  let lastSweepAt = 0;
  let trailingTimer = null;
  let debounceTimer = null;

  // The snapshot must mirror the line verbatim, minus itself. Insertion order is kept, so
  // once a line has been rewritten the recomputed value is string-identical and the sweep
  // terminates on the equality fast path below.
  function desiredSnapshot(properties) {
    const mirror = {};
    Object.keys(properties).forEach((key) => {
      if (key !== SNAPSHOT_KEY) mirror[key] = properties[key];
    });
    return JSON.stringify(mirror);
  }

  // Only lines the cart transform manages. A malformed snapshot (not a JSON object) is
  // Zepto state this module does not understand — leave it alone.
  function needsRewrite(item) {
    const props = item && item.properties;
    if (!props || typeof props !== 'object') return false;
    const snapshot = props[SNAPSHOT_KEY];
    if (typeof snapshot !== 'string' || snapshot === '') return false;
    try {
      const parsed = JSON.parse(snapshot);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    } catch (error) {
      return false;
    }
    return snapshot !== desiredSnapshot(props);
  }

  // Stable across our own write (which only changes the snapshot), so the attempt counter
  // recognises the same line before and after a rewrite.
  function lineIdentity(item) {
    const props = item.properties || {};
    const pairs = Object.keys(props)
      .filter((key) => key !== SNAPSHOT_KEY)
      .sort()
      .map((key) => `${key}=${String(props[key])}`);
    return `${item.id}|${pairs.join(UNIT)}`;
  }

  function readAttempts() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeAttempts(attempts) {
    try {
      const keys = Object.keys(attempts);
      if (keys.length > STORE_LIMIT) {
        keys.slice(0, keys.length - STORE_LIMIT).forEach((key) => delete attempts[key]);
      }
      sessionStorage.setItem(STORE_KEY, JSON.stringify(attempts));
    } catch (error) {
      /* Safari private mode — the equality fast path is what actually ends a loop. */
    }
  }

  // change.js RESETS an omitted `quantity` to 1, so the line's own quantity is resent.
  function changeLine(key, quantity, properties) {
    return fetch(window.routes.cart_change_url, {
      ...window.fetchConfig(),
      body: JSON.stringify({ id: key, quantity, properties }),
    }).then((response) => response.json());
  }

  async function rewriteAll(candidates, cart) {
    const expectedItemCount = cart.item_count;
    const limit = Math.min(candidates.length, MAX_LINES_PER_SWEEP);
    let live = cart;
    let changed = false;

    for (let i = 0; i < limit; i++) {
      // Re-resolve against the cart the previous call returned: every rewrite re-keys a
      // line, so a key captured at sweep start is only valid once.
      const identity = lineIdentity(candidates[i]);
      const item = live.items.find((candidate) => lineIdentity(candidate) === identity);
      if (!item || !needsRewrite(item)) continue;

      const properties = { ...item.properties, [SNAPSHOT_KEY]: desiredSnapshot(item.properties) };
      const state = await changeLine(item.key, item.quantity, properties);
      if (state && (state.status || state.errors)) throw new Error(state.description || 'cart error');

      if (state.item_count !== expectedItemCount) {
        disabled = true;
        console.error('[byRavn-order-properties] item_count changed', expectedItemCount, state.item_count);
        throw new Error('item_count invariant broken');
      }

      changed = true;
      live = state;
    }
    return changed;
  }

  async function sweep() {
    if (disabled || busy) return;
    const now = Date.now();
    if (now - lastSweepAt < MIN_SWEEP_INTERVAL_MS) {
      clearTimeout(trailingTimer);
      trailingTimer = setTimeout(sweep, MIN_SWEEP_INTERVAL_MS - (now - lastSweepAt));
      return;
    }

    let cart;
    try {
      cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
    } catch (error) {
      lastSweepAt = Date.now();
      return;
    }
    if (!cart || !Array.isArray(cart.items)) {
      lastSweepAt = Date.now();
      return;
    }

    let candidates = cart.items.filter(needsRewrite);
    // Terminating fast path: after a rewrite the recomputed snapshot is string-identical,
    // so the sweep our own repaint re-triggers dies here.
    if (!candidates.length) {
      lastSweepAt = Date.now();
      return;
    }

    const attempts = readAttempts();
    candidates = candidates.filter((item) => (attempts[lineIdentity(item)] || 0) < MAX_ATTEMPTS);
    if (!candidates.length) {
      lastSweepAt = Date.now();
      return;
    }
    candidates.forEach((item) => {
      const identity = lineIdentity(item);
      attempts[identity] = (attempts[identity] || 0) + 1;
    });
    writeAttempts(attempts);

    busy = true;
    window.byRavnCart.setBusy(true);
    try {
      const changed = await rewriteAll(candidates, cart);
      // A rewrite re-keys the line; without a repaint the rendered quantity/remove
      // controls keep posting the stale key and 400. Same flow as the strip.
      const surface = window.byRavnCart.surfaces()[0];
      if (changed && surface) {
        window.byRavnCart.paintAllSurfaces(await window.byRavnCart.fetchCartSections(), surface, PUBLISH_SOURCE);
      }
    } catch (error) {
      console.error('[byRavn-order-properties]', error);
      window.byRavnCart.surfaces().forEach((surface) => surface.onCartUpdate());
    } finally {
      busy = false;
      window.byRavnCart.setBusy(false);
      lastSweepAt = Date.now();
    }
  }

  function scheduleSweep(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sweep, delay == null ? SWEEP_DEBOUNCE_MS : delay);
  }

  function boot() {
    // A rendered cart line is the cheap hint that the cart is non-empty; the sweep still
    // fetches and decides for itself. Covers the customer who added on a previous visit
    // and comes back straight to /cart.
    if (document.querySelector('.cart-item')) scheduleSweep(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Every theme-driven mutation: Dawn's updateQuantity, the gavepose toggle, the
  // gravering reconciler, the preview strip's repaint. Skip only our own publish;
  // everyone else's may have changed properties.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && event.source === PUBLISH_SOURCE) return;
    scheduleSweep();
  });

  // Zepto's add attaches the preview and the snapshot out-of-band after the add returns;
  // the strip then reacts at 700/2200 ms. Sweep after each of its passes.
  document.addEventListener('byRavn:zepto-add', () => {
    setTimeout(() => {
      sweep();
      setTimeout(() => {
        if (!disabled) sweep();
      }, ZEPTO_ADD_RETRY_MS);
    }, ZEPTO_ADD_DELAY_MS);
  });
})();
